import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput } from '@/components/ui/phoneInput'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { usePhoneField } from '@/hooks/usePhoneField'
import { parseError } from '@/lib/errors'
import {
  CARRIER_INDUSTRY_LABELS,
  CarrierIndustry,
  InsuranceCarrier,
  PET_HIDDEN_PROFILE_FIELDS,
  useCreateCarrier,
  useDeleteCarrierAlias,
  useDeleteCarrierContact,
  useUpdateCarrier,
  useUpsertCarrierAlias,
  useUpsertCarrierContact,
} from '@/models/insuranceCarrier'
import { validatePhone } from '@/utils/phone'
import { CarrierAliasRows, EditableAlias } from './CarrierAliasRows'
import { CarrierContactRows, EditableContact } from './CarrierContactRows'
import { CarrierLogoUpload } from './CarrierLogoUpload'
import { CarrierProfileFields, EditableProfile, EMPTY_PROFILE } from './CarrierProfileFields'

interface CarrierFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  carrier: InsuranceCarrier | null
}

export function CarrierFormDialog({ open, onOpenChange, carrier }: CarrierFormDialogProps) {
  const createCarrier = useCreateCarrier()
  const updateCarrier = useUpdateCarrier()
  const upsertContact = useUpsertCarrierContact()
  const deleteContact = useDeleteCarrierContact()
  const upsertAlias = useUpsertCarrierAlias()
  const deleteAlias = useDeleteCarrierAlias()

  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [naicCode, setNaicCode] = useState('')
  const [website, setWebsite] = useState('')
  const [primaryEmail, setPrimaryEmail] = useState('')
  const [primaryPhone, setPrimaryPhone] = useState('')
  const [notes, setNotes] = useState('')
  const [logo, setLogo] = useState<File | null>(null)
  const [marketingLogo, setMarketingLogo] = useState<File | null>(null)
  const [isWholesaler, setIsWholesaler] = useState(false)
  const [industry, setIndustry] = useState<CarrierIndustry>(CarrierIndustry.TRUCKING)
  const [profile, setProfile] = useState<EditableProfile>(EMPTY_PROFILE)
  const [contacts, setContacts] = useState<EditableContact[]>([])
  const [aliases, setAliases] = useState<EditableAlias[]>([])
  const [contactPhoneErrors, setContactPhoneErrors] = useState<(string | undefined)[]>([])
  const { error: primaryPhoneError, clear: clearPrimaryPhone, check: checkPrimaryPhone } = usePhoneField()
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setContactPhoneErrors([])
    clearPrimaryPhone()
    setName(carrier?.name ?? '')
    setDisplayName(carrier?.displayName ?? '')
    setNaicCode(carrier?.naicCode ?? '')
    setWebsite(carrier?.website ?? '')
    setPrimaryEmail(carrier?.primaryEmail ?? '')
    setPrimaryPhone(carrier?.primaryPhone ?? '')
    setNotes(carrier?.notes ?? '')
    setLogo(null)
    setMarketingLogo(null)
    setIsWholesaler(carrier?.isWholesaler ?? false)
    setIndustry(carrier?.industry ?? CarrierIndustry.TRUCKING)
    setProfile(
      carrier
        ? {
            rating: carrier.rating,
            coiSpeed: carrier.coiSpeed,
            admittedStatus: carrier.admittedStatus,
            publicNote: carrier.publicNote,
            financingOptions: carrier.financingOptions,
            allowsIndividualCoveragePurchase: carrier.allowsIndividualCoveragePurchase,
            claimsHasMultipleWaysToFile: carrier.claimsHasMultipleWaysToFile,
            claimsWaysToFile: carrier.claimsWaysToFile,
            claimsHas247Support: carrier.claimsHas247Support,
            claimsSupportAvailability: carrier.claimsSupportAvailability,
            claimsHasPayoutInformation: carrier.claimsHasPayoutInformation,
            claimsPayoutSpeed: carrier.claimsPayoutSpeed,
            claimsWaysToGetPaid: carrier.claimsWaysToGetPaid,
          }
        : EMPTY_PROFILE
    )
    setContacts(
      (carrier?.contacts ?? []).map((contact) => ({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        title: contact.title,
        isPrimary: contact.isPrimary,
      }))
    )
    setAliases(
      (carrier?.aliases ?? []).map((alias) => ({
        id: alias.id,
        name: alias.name,
        admittedStatus: alias.admittedStatus,
        notes: alias.notes,
      }))
    )
  }, [open, carrier, clearPrimaryPhone])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const primaryPhoneOk = checkPrimaryPhone(primaryPhone)
    // Nameless rows are dropped on save, so an unfinished one must not block it.
    const phoneErrors = contacts.map((contact) =>
      contact.name.trim() ? validatePhone(contact.phone) : undefined
    )
    setContactPhoneErrors(phoneErrors)
    if (!primaryPhoneOk || phoneErrors.some(Boolean)) return
    setSaving(true)
    try {
      // A pet carrier has no wholesaler flag or trucking paper details; they're left as stored, not blanked.
      const petHidden = pet ? omitPetHidden(profile) : profile
      const input = {
        name,
        displayName,
        naicCode,
        website,
        primaryEmail,
        primaryPhone,
        notes,
        logo,
        marketingLogo,
        industry,
        ...(pet ? {} : { isWholesaler }),
        // Omitted, not blanked, for a wholesaler: the PATCH leaves what is stored alone.
        ...(wholesaler ? {} : petHidden),
      }
      const saved = carrier
        ? await updateCarrier.mutateAsync({ id: carrier.id, ...input })
        : await createCarrier.mutateAsync(input)

      // Reconcile contacts: delete removed ones, then upsert the rest.
      const keptIds = contacts.filter((c) => c.id).map((c) => c.id as string)
      const removed = (carrier?.contacts ?? []).map((c) => c.id).filter((id) => !keptIds.includes(id))
      for (const contactId of removed) {
        await deleteContact.mutateAsync({ carrierId: saved.id, contactId })
      }
      for (const contact of contacts) {
        if (!contact.name.trim()) continue
        await upsertContact.mutateAsync({
          carrierId: saved.id,
          contactId: contact.id,
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          title: contact.title,
          isPrimary: contact.isPrimary,
        })
      }

      // Reconcile aliases the same way: delete removed ones, then upsert the rest.
      const keptAliasIds = aliases.filter((a) => a.id).map((a) => a.id as string)
      const removedAliases = (carrier?.aliases ?? [])
        .map((a) => a.id)
        .filter((id) => !keptAliasIds.includes(id))
      for (const aliasId of removedAliases) {
        await deleteAlias.mutateAsync({ carrierId: saved.id, aliasId })
      }
      for (const alias of aliases) {
        if (!alias.name.trim()) continue
        await upsertAlias.mutateAsync({
          carrierId: saved.id,
          aliasId: alias.id,
          name: alias.name,
          admittedStatus: alias.admittedStatus,
          notes: alias.notes,
        })
      }

      toast.success(carrier ? 'Carrier updated' : 'Carrier created')
      onOpenChange(false)
    } catch (err: unknown) {
      toast.error(parseError(err))
    } finally {
      setSaving(false)
    }
  }

  const pet = industry === CarrierIndustry.PET
  const wholesaler = !pet && isWholesaler

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{carrier ? 'Edit carrier' : 'Add carrier'}</DialogTitle>
            <DialogDescription>Insurance carrier details, aliases and contacts.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-col gap-4">
            {/* First: it decides which of the fields below a carrier keeps. */}
            <div className="flex flex-col gap-2">
              <Label htmlFor="carrier-industry">Insurance industry</Label>
              <Select value={industry} onValueChange={(next) => setIndustry(next as CarrierIndustry)}>
                <SelectTrigger id="carrier-industry" className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(CarrierIndustry).map((value) => (
                    <SelectItem key={value} value={value}>
                      {CARRIER_INDUSTRY_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="carrier-name">Name</Label>
              <Input id="carrier-name" value={name} onChange={(e) => setName(e.target.value)} required />
              <p className="text-muted-foreground text-xs">
                Internal identifier — used to match this carrier to vendor submissions.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="carrier-display-name">Customer-facing name</Label>
              <Input
                id="carrier-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                This will display on Quote Cards, Indication Cards (carrier estimates), and Cai estimates.
                Leave blank to use Name.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="carrier-naic">NAIC code</Label>
                <Input id="carrier-naic" value={naicCode} onChange={(e) => setNaicCode(e.target.value)} />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="carrier-website">Website</Label>
                <Input
                  id="carrier-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="carrier-email">Primary email</Label>
                <Input
                  id="carrier-email"
                  type="email"
                  value={primaryEmail}
                  onChange={(e) => setPrimaryEmail(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Primary phone</Label>
                <PhoneInput
                  value={primaryPhone}
                  onChange={(value) => setPrimaryPhone(value || '')}
                  error={!!primaryPhoneError}
                />
                <FieldError message={primaryPhoneError} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <CarrierLogoUpload
                label="Logo"
                currentUrl={carrier?.logo}
                file={logo}
                onChange={setLogo}
                hint="Used in-app on Quote Cards and Estimate Cards."
              />
              <CarrierLogoUpload
                label="Marketing logo (greyscale)"
                currentUrl={carrier?.marketingLogo}
                file={marketingLogo}
                onChange={setMarketingLogo}
                hint="Used on the public marketing site's carrier logo wall. Should be a greyscale/monochrome version."
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="carrier-notes">Internal notes</Label>
              <Textarea id="carrier-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <p className="text-muted-foreground text-xs">Never shown to customers.</p>
            </div>
            {!pet && (
              <>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={isWholesaler}
                    onCheckedChange={(checked) => setIsWholesaler(checked === true)}
                  />
                  Wholesaler
                </label>
                <p className="text-muted-foreground -mt-2 text-xs">
                  Excludes this carrier from the weekly automated carrier-details research refresh.
                </p>
              </>
            )}
            {wholesaler ? (
              <p className="text-muted-foreground text-xs">
                Carrier details (insurance type, rating, COI speed, financing and claims capabilities) are
                hidden for wholesalers — a wholesaler never appears to a customer as the insurer writing the
                risk.
              </p>
            ) : (
              <CarrierProfileFields
                value={profile}
                onChange={(patch) => setProfile((current) => ({ ...current, ...patch }))}
                pet={pet}
              />
            )}
            <CarrierAliasRows aliases={aliases} onChange={setAliases} />
            <CarrierContactRows
              contacts={contacts}
              onChange={(next) => {
                setContacts(next)
                // phoneErrors is index-keyed, so any add/remove/edit invalidates it.
                setContactPhoneErrors([])
              }}
              phoneErrors={contactPhoneErrors}
            />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !name.trim()} loading={saving ? 'Saving…' : undefined}>
              {carrier ? 'Save changes' : 'Create carrier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function omitPetHidden(profile: EditableProfile): Partial<EditableProfile> {
  const kept: Partial<EditableProfile> = { ...profile }
  for (const field of PET_HIDDEN_PROFILE_FIELDS) delete kept[field]
  return kept
}
